import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { AuthBackground } from "@/components/auth/AuthBackground";
import { AuthHandoffOverlay, BootSplash } from "@/components/AppShellLoadingOverlay";
import {
  BOOT_RELEASE_DURATION_MS,
  BOOT_RELEASE_EASING,
  BOOT_ROUTE_READY_FAIL_OPEN_MS,
  markBootWarmSession,
} from "@/lib/bootTiming";
import { isRevealTargetAlias } from "@/lib/revealRouting";
import { cn } from "@/lib/utils";

export type BootVisibilityState = "boot_visible" | "boot_releasing" | "boot_complete";
export type AuthHandoffState = "auth_handoff_pending" | "auth_handoff_complete";

type RevealTargetOptions = {
  requiresTopChrome: boolean;
};

type AppRevealContextValue = {
  bootState: BootVisibilityState;
  authHandoffState: AuthHandoffState;
  setAuthResolved: (ready: boolean) => void;
  setEntitlementsResolved: (ready: boolean) => void;
  prepareForRevealTarget: (targetKey: string, options: RevealTargetOptions) => void;
  markRouteReady: (targetKey: string) => void;
  markTopChromeReady: (targetKey: string) => void;
  beginAuthHandoff: (targetKey: string, options: RevealTargetOptions) => void;
  completeAuthHandoff: () => void;
};

const AppRevealContext = createContext<AppRevealContextValue | null>(null);

interface AppRevealProviderProps {
  children: ReactNode;
  shellReady: boolean;
  appModuleReady: boolean;
  minimumBootElapsed: boolean;
}

export function AppRevealProvider({
  children,
  shellReady,
  appModuleReady,
  minimumBootElapsed,
}: AppRevealProviderProps) {
  const [bootState, setBootState] = useState<BootVisibilityState>("boot_visible");
  const [authHandoffState, setAuthHandoffState] =
    useState<AuthHandoffState>("auth_handoff_complete");
  const [authResolved, setAuthResolved] = useState(true);
  const [entitlementsResolved, setEntitlementsResolved] = useState(true);
  const [routeReady, setRouteReady] = useState(false);
  const [topChromeReady, setTopChromeReady] = useState(true);
  const activeTargetKeyRef = useRef("");
  const releaseTimeoutRef = useRef<number | null>(null);

  const prepareForRevealTarget = useCallback(
    (targetKey: string, options: RevealTargetOptions) => {
      if (activeTargetKeyRef.current === targetKey) {
        if (!options.requiresTopChrome) {
          setTopChromeReady(true);
        }
        return;
      }

      activeTargetKeyRef.current = targetKey;
      setRouteReady(false);
      setTopChromeReady(!options.requiresTopChrome);
    },
    [],
  );

  const markRouteReady = useCallback((targetKey: string) => {
    if (!isRevealTargetAlias(activeTargetKeyRef.current, targetKey)) {
      return;
    }

    setRouteReady(true);
  }, []);

  const markTopChromeReady = useCallback((targetKey: string) => {
    if (activeTargetKeyRef.current !== targetKey) {
      return;
    }

    setTopChromeReady(true);
  }, []);

  const beginAuthHandoff = useCallback(
    (targetKey: string, options: RevealTargetOptions) => {
      setAuthHandoffState("auth_handoff_pending");
      prepareForRevealTarget(targetKey, options);
    },
    [prepareForRevealTarget],
  );

  const completeAuthHandoff = useCallback(() => {
    setAuthHandoffState("auth_handoff_complete");
  }, []);

  const bootDependenciesReady =
    shellReady &&
    appModuleReady &&
    minimumBootElapsed &&
    authResolved &&
    entitlementsResolved &&
    routeReady &&
    topChromeReady;

  const bootSoftDependenciesReady =
    shellReady &&
    appModuleReady &&
    minimumBootElapsed &&
    authResolved &&
    entitlementsResolved &&
    topChromeReady;

  const shouldStartBootRelease = bootDependenciesReady;

  useEffect(() => {
    if (bootState !== "boot_visible" || !bootSoftDependenciesReady || routeReady) {
      return;
    }

    const failOpenId = window.setTimeout(() => {
      setRouteReady(true);
    }, BOOT_ROUTE_READY_FAIL_OPEN_MS);

    return () => {
      window.clearTimeout(failOpenId);
    };
  }, [bootState, bootSoftDependenciesReady, routeReady]);

  useEffect(() => {
    if (bootState !== "boot_complete") {
      return;
    }
    markBootWarmSession();
  }, [bootState]);

  useEffect(() => {
    if (bootState !== "boot_visible" || !shouldStartBootRelease) {
      return;
    }

    setBootState("boot_releasing");
  }, [bootState, shouldStartBootRelease]);

  useEffect(() => {
    if (bootState !== "boot_releasing") {
      return;
    }

    releaseTimeoutRef.current = window.setTimeout(() => {
      setBootState("boot_complete");
      releaseTimeoutRef.current = null;
    }, BOOT_RELEASE_DURATION_MS);

    return () => {
      if (releaseTimeoutRef.current !== null) {
        window.clearTimeout(releaseTimeoutRef.current);
        releaseTimeoutRef.current = null;
      }
    };
  }, [bootState]);

  useEffect(() => {
    if (
      authHandoffState === "auth_handoff_pending" &&
      authResolved &&
      entitlementsResolved &&
      routeReady &&
      topChromeReady
    ) {
      setAuthHandoffState("auth_handoff_complete");
    }
  }, [authHandoffState, authResolved, entitlementsResolved, routeReady, topChromeReady]);

  const value = useMemo<AppRevealContextValue>(
    () => ({
      bootState,
      authHandoffState,
      setAuthResolved,
      setEntitlementsResolved,
      prepareForRevealTarget,
      markRouteReady,
      markTopChromeReady,
      beginAuthHandoff,
      completeAuthHandoff,
    }),
    [
      authHandoffState,
      beginAuthHandoff,
      bootState,
      completeAuthHandoff,
      markRouteReady,
      markTopChromeReady,
      prepareForRevealTarget,
    ],
  );

  return (
    <AppRevealContext.Provider value={value}>
      {authHandoffState === "auth_handoff_pending" ? (
        <>
          <AuthBackground layer="handoff" className="z-[1000]" />
          <AuthHandoffOverlay />
        </>
      ) : null}
      {children}
      {bootState !== "boot_complete" ? (
        <BootSplash
          style={{
            transitionDuration: `${BOOT_RELEASE_DURATION_MS}ms`,
            transitionTimingFunction: BOOT_RELEASE_EASING,
          }}
          className={cn(
            "transition-opacity",
            bootState === "boot_visible" ? "opacity-100" : "pointer-events-none opacity-0",
          )}
        />
      ) : null}
    </AppRevealContext.Provider>
  );
}

export function useOptionalAppReveal() {
  return useContext(AppRevealContext);
}

export function useAppReveal() {
  const context = useOptionalAppReveal();

  if (!context) {
    throw new Error("useAppReveal must be used within an AppRevealProvider");
  }

  return context;
}
